import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../../ui/utils";

export type CellAlign = "left" | "right" | "center";

const ALIGN: Record<CellAlign, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

/**
 * Comfortable minimum width (px) for a status/severity pill column (has a leading
 * dot) so a chip like "Non-compliant" or "Critical" never wraps, overlaps, or
 * clips. Accounts for the cell's px-3 inset plus the pill chrome (dot, gap, pad).
 */
export function badgeMinWidth(chars: number): number {
  return Math.min(156, Math.max(90, Math.round(chars * 7 + 46)));
}

/** Comfortable minimum width (px) for a type/category chip column (no dot), so it
 *  stays a touch tighter than a status pill while still never clipping. */
export function typeMinWidth(chars: number): number {
  return Math.min(150, Math.max(78, Math.round(chars * 6.5 + 34)));
}

/** Comfortable minimum width (px) for a compact numeric column ("94%", "1,204").
 *  `chars` is the longest of the header and the values, so the width also fits a
 *  wide header like "CONTROLS"/"GROUPS" (which is nowrap and must not
 *  clip). Still tight enough that many-column tables stay tables. */
export function numericMinWidth(chars: number): number {
  return Math.min(140, Math.max(58, Math.round(chars * 7 + 30)));
}

/** Preferred width a flexible (title/text) column takes when there is room; the
 *  title earns more than a secondary text column. Content longer than this wraps
 *  onto multiple lines - it is never truncated. */
const TITLE_COMFORT = 240;
const TEXT_COMFORT = 176;
/** Readable wrap floor for a flexible column, and the width each flexible column
 *  must be able to keep before the whole table stacks. Kept low so tables are
 *  strongly preferred - a wrapping title column reads fine, whereas stacking a
 *  wide dataset into cards is more confusing. */
const FLEX_MIN = 48;

export interface AssistantTableColumn {
  align?: CellAlign;
  /**
   * Keep this column's body cells on a single line (numbers, percentages, status
   * pills, rank badges) so they stay compact and atomic. Flexible free-text /
   * title columns leave this off so their full value wraps across lines.
   */
  noWrap?: boolean;
  /**
   * Comfortable minimum width (px) for a fixed column (badge / numeric / rank).
   * Flexible columns (grow > 0) size themselves from the constants above instead.
   */
  minPx?: number;
  /** Share of leftover horizontal space among flexible columns. Title is high. */
  grow?: number;
  /** The primary column - gets the most width and the title typography. */
  isTitle?: boolean;
  cellClassName?: string;
}

/** Shared neutral-block surface, identical to ChartMessage, so data blocks read
 *  as one consistent family rather than independent floating widgets. */
const SURFACE = "border-border/50 bg-muted/30 dark:border-white/[0.06] dark:bg-white/[0.02]";

/** Measure the live pixel width available to the table so column widths can
 *  adapt to the panel's real size (full / compact / mobile), not a breakpoint. */
function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/**
 * The single presentation shell for every table the assistant renders, tuned for
 * a floating enterprise chat panel where every value must be fully readable with
 * no extra interaction:
 *
 * - Nothing is truncated, hidden, or hover-gated: badge / numeric / rank columns
 *   stay compact on one line; title and text columns wrap across as many lines as
 *   needed so their full value is always visible.
 * - Content-priority widths: fixed columns hold their compact minimums, the title
 *   and text columns absorb the rest (the title first).
 * - No horizontal scroll: when the columns cannot fit the measured width readably,
 *   the table transforms into a stacked responsive table - one record per block,
 *   every field shown as an aligned label/value pair.
 * - Perfect header/body alignment via `table-fixed` + a shared colgroup; sticky
 *   header and vertical scroll for long tables. All rows render (no pagination),
 *   so the complete dataset is always present in the response.
 */
export function AssistantTable({
  columns,
  header,
  rows,
}: {
  columns: AssistantTableColumn[];
  header: ReactNode[];
  rows: ReactNode[][];
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const colCount = columns.length;

  const isFlex = (i: number) => (columns[i]?.grow ?? 0) > 0;
  const fixedMinOf = (i: number) => columns[i]?.minPx ?? 96;
  const comfortOf = (i: number) =>
    isFlex(i) ? (columns[i]?.isTitle ? TITLE_COMFORT : TEXT_COMFORT) : fixedMinOf(i);

  const flexIdx = columns.map((_, i) => i).filter(isFlex);
  const fixedTotal = columns.reduce((s, _, i) => s + (isFlex(i) ? 0 : fixedMinOf(i)), 0);
  const comfortTotal = columns.reduce((s, _, i) => s + comfortOf(i), 0);
  const sumMin = fixedTotal + flexIdx.length * FLEX_MIN;

  // Stack when the flexible columns cannot each keep a readable wrap width - i.e.
  // the table would be too cramped to fit all columns. `width === 0` is the
  // pre-measure frame (useLayoutEffect fills it in before paint) so default to
  // the table there to avoid a flash.
  const stacked = width > 0 && colCount > 1 && width < sumMin;

  if (stacked) {
    return (
      <div ref={ref} className="my-1.5">
        <StackedTable columns={columns} header={header} rows={rows} />
      </div>
    );
  }

  // Table mode: fixed columns at their min; flexible columns take the rest (the
  // title first) - at comfort width when there is room (table hugs its content),
  // otherwise sharing the container so their text simply wraps to more lines.
  const measured = width > 0;
  let pxWidths = columns.map((_, i) => comfortOf(i));
  let tableWidth = comfortTotal;
  if (measured) {
    if (width >= comfortTotal) {
      pxWidths = columns.map((_, i) => comfortOf(i));
      tableWidth = comfortTotal;
    } else {
      const growTotal = flexIdx.reduce((s, i) => s + (columns[i].grow ?? 1), 0) || 1;
      const extra = Math.max(0, width - fixedTotal - flexIdx.length * FLEX_MIN);
      pxWidths = columns.map((c, i) =>
        isFlex(i) ? FLEX_MIN + extra * ((c.grow ?? 1) / growTotal) : fixedMinOf(i),
      );
      tableWidth = width;
    }
  }
  const pct = (i: number) => (pxWidths[i] / (tableWidth || 1)) * 100;

  const cellCls = (i: number) =>
    cn(
      "px-3 py-1.5 align-top",
      columns[i]?.noWrap ? "whitespace-nowrap" : "break-words",
      ALIGN[columns[i]?.align ?? "left"],
      columns[i]?.cellClassName,
    );

  return (
    <div ref={ref} className="my-1.5">
      <div
        className={cn(
          "scrollbar-neutral max-h-[24rem] max-w-full overflow-x-auto overflow-y-auto overscroll-contain rounded-xl border supports-[backdrop-filter]:backdrop-blur-sm",
          SURFACE,
        )}
        style={measured ? { width: tableWidth } : undefined}
      >
        <table className="w-full table-fixed border-separate border-spacing-0 text-[0.75rem] leading-snug text-foreground">
          <colgroup>
            {columns.map((_, i) => (
              <col key={i} style={measured ? { width: `${pct(i)}%` } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {Array.from({ length: colCount }, (_, i) => (
                <th
                  key={i}
                  scope="col"
                  className={cn(
                    cellCls(i),
                    // Quiet, integrated header: an opaque muted bar (so scrolling
                    // rows pass cleanly beneath the sticky header) with a small
                    // label - reads as content, not a dashboard bar.
                    "sticky top-0 z-10 border-b border-border/60 py-2 text-[0.6875rem] font-semibold tracking-wide text-muted-foreground",
                    "bg-secondary/95 backdrop-blur-sm dark:border-white/10 dark:bg-[#1b1830]/95",
                  )}
                >
                  {header[i] ?? null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr
                key={r}
                className={cn(
                  "transition-colors last:[&>td]:border-b-0",
                  "even:bg-muted/20 hover:bg-primary/[0.04]",
                  "dark:even:bg-white/[0.02] dark:hover:bg-white/[0.04]",
                )}
              >
                {Array.from({ length: colCount }, (_, c) => (
                  <td key={c} className={cn(cellCls(c), "border-b border-border/25")}>
                    {row[c] ?? null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Stacked responsive table: when the columns cannot fit the width, each row
 * becomes a block that lists every field as an aligned label/value pair, so the
 * complete record is readable top-to-bottom - no truncation, no horizontal
 * scroll, no interaction. Records share one bordered surface divided by hairlines
 * to preserve a clean, single-table appearance. Values wrap freely; badges and
 * numbers stay compact.
 */
function StackedTable({
  columns,
  header,
  rows,
}: {
  columns: AssistantTableColumn[];
  header: ReactNode[];
  rows: ReactNode[][];
}) {
  const titleIdx = Math.max(
    0,
    columns.findIndex((c) => c.isTitle),
  );
  return (
    <div
      className={cn(
        "scrollbar-neutral max-h-[24rem] overflow-y-auto overscroll-contain rounded-xl border",
        SURFACE,
      )}
    >
      {rows.map((row, r) => (
        <div
          key={r}
          className={cn("px-3 py-2.5", r > 0 && "border-t border-border/40 dark:border-white/10")}
        >
          <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
            {columns.map((_, i) => (
              <Fragment key={i}>
                <dt className="whitespace-nowrap text-[0.6875rem] font-medium tracking-wide text-muted-foreground">
                  {header[i] ?? null}
                </dt>
                <dd
                  className={cn(
                    "min-w-0 break-words text-[0.75rem] text-foreground",
                    i === titleIdx ? "font-semibold" : "font-medium",
                  )}
                >
                  {row[i] ?? null}
                </dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}
